import axios from 'axios';

import Api from './Api';
import { ENDPOINTS } from 'utils/api';
import Portrait from './Portrait';
import { BrandName, UserRoles } from 'utils/types';

export default class Auth {
    static current = () => {
        const now = new Date();
        const token = localStorage.getItem('auth.token');
        const expires = parseInt(localStorage.getItem('auth.expires') || '0');
        const userId = Auth.getUserInfo('userId');
        const insurerId = Auth.getUserInfo('insurerId');

        return token !== undefined && token !== null && insurerId !== undefined && insurerId !== null && expires !== undefined && userId !== undefined && now.getTime() < expires;
    };

    static heartbeat = () => {
        const now = new Date();
        const expires = parseInt(localStorage.getItem('auth.expires') || '0');
        let expiresIn = expires - now.getTime();
        if (expiresIn < 0) expiresIn = 0;

        return { current: Auth.current(), expiresIn: expiresIn };
    };

    static login = (email, password, brandName) => {
        return new Promise((resolve, reject) => {
            const postData = {
                Insurer: brandName,
                Username: email,
                Password: password,
            };

            axios
                .post(`${process.env.REACT_APP_API_BASE}/auth`, postData)
                .then(response => {
                    if (response.status === 200) {
                        const result = Auth.processTokenRequest(response.data.Data, brandName);
                        resolve(result);
                    } else {
                        reject(response.data.Errors);
                    }
                })
                .catch(error => {
                    reject([error]);
                });
        });
    };

    static refresh = () => {
        return new Promise((resolve, reject) => {
            const abortController = new AbortController();

            const brand = localStorage.getItem('auth.brand');
            if (brand === undefined || brand === null) {
                console.error("Can't refresh token without brand - none found in local storage");
                reject(false);
            }

            Api.get(ENDPOINTS.RefreshToken, abortController)
                .then((response: any) => {
                    const result = Auth.processTokenRequest(response.Data, brand!);
                    resolve(result);
                })
                .catch(error => {
                    reject(error);
                });
        });
    };

    private static processTokenRequest = (data: any, brandName: string) => {
        if (data.AccessToken !== undefined && data.Expires !== undefined) {
            const userInfo = {
                id: data.UserDetails.Id,
                userId: data.UserDetails.UserId,
                portraitId: data.UserDetails.PortraitId,
                insurerId: data.UserDetails.InsurerId,
                firstName: data.UserDetails.FirstName,
                lastName: data.UserDetails.LastName,
                initials: data.UserDetails.Initials,
                email: data.UserDetails.Email,
                roles: data.UserDetails.Roles.map(role => role.Service),
            };

            Auth.setToken(data.AccessToken, data.Expires, userInfo, brandName);

            return true;
        } else {
            return false;
        }
    };

    static logout = () => {
        localStorage.removeItem('auth.token');
        localStorage.removeItem('auth.expires');
        localStorage.removeItem('auth.user_info');
        localStorage.removeItem('auth.portrait');
        localStorage.removeItem('underwriters_v2');
        localStorage.removeItem('condition_prepops');

        const keys = Object.keys(localStorage);

        keys.forEach(key => {
            if (key.indexOf('quote.') === 0) {
                localStorage.removeItem(key);
            }
        });
    };

    private static setToken = (token, expires, userInfo, brandName) => {
        localStorage.setItem('auth.token', token);
        localStorage.setItem('auth.expires', expires + '000');
        localStorage.setItem('auth.user_info', JSON.stringify(userInfo));
        localStorage.setItem('auth.brand', brandName);
    };

    static getUserInfo = param => {
        const info = Auth.getAllUserInfo();
        return info ? info[param] : null;
    };

    static hasRole = role => {
        const info = Auth.getUserInfo('roles');
        if (!info) return false;
        return info.indexOf(role) > -1;
    };

    static userRoles = () => {
        const info = Auth.getUserInfo('roles');
        if (!info) return [];
        return info as UserRoles[];
    };

    static getAllUserInfo = () => {
        try {
            const infoStr = localStorage.getItem('auth.user_info');
            if (infoStr === null) return null;
            const info = JSON.parse(infoStr);
            return info;
        } catch (e) {
            return null;
            // this.logout(); // TODO: do we log out users and start again if the user info is corrupted?
        }
    };

    static getCurrentBrandName: () => BrandName | null = () => {
        const b = localStorage.getItem('auth.brand');
        if (b === null) return null;
        return b as BrandName;
    };

    static getPortrait = () => {
        const portraitId = Auth.getUserInfo('portraitId');
        const portrait = new Portrait();
        portrait.id = portraitId;

        return portrait.get();
    };
}
